/**
 * The header's level + credits pills need real numbers, and `GET /api/cosmetics` already
 * returns them (`Progress` in `armoury.ts`) — it is an approved Batch 1 endpoint, not a new
 * screen. Rather than have the header and the armoury route each fetch it独立ly and drift, this
 * is one small store both read and write, using core's own `createStore` (its doc comment
 * invites exactly this: "Game developers can extend this by creating new stores").
 *
 * Scope note: this refreshes on shell mount and after an armoury equip/purchase. A shop spend
 * elsewhere would also move `softBalance`, but the shop is Batch 2 — until it exists there is
 * nothing else that can change these numbers, so no further invalidation is wired yet.
 */
import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';
import { NO_CARD } from '../../../../shared/src/player-cards.js';

export interface PlayerProgress {
  level: number;
  rankPoints: number;
  /** Credits — earned from matches and quests, spent on skins, titles and weapons (`SOFT`). */
  softBalance: number;
  /** Coins — what `POST /topup/checkout` sells, spent on finishes and charms (`PREMIUM`). */
  premiumBalance: number;
  /** The worn player card, `NO_CARD` (Standard Issue) when nothing is equipped or the call failed. */
  equippedCard: number;
  /** The worn title's display NAME (never the slug), or `null` when nothing is equipped or the
   * call failed. */
  equippedTitle: string | null;
}

interface CosmeticsResponse {
  progress: Omit<PlayerProgress, 'equippedCard' | 'equippedTitle'>;
}

export const playerProgressStore = createStore<PlayerProgress | null>(null);

/**
 * Fetches current progress and publishes it. Failures are swallowed — the header falls back
 * to its placeholder dashes rather than throwing over a value nothing else depends on.
 *
 * The card and title calls ride alongside the balance/level one but are caught on their own,
 * same posture Career already takes around its own `/player-cards` and `/game/titles` reads
 * (`career-route.ts`): an outage on either must cost this store a worn cosmetic, never the
 * level pill everything else here depends on.
 */
export async function refreshPlayerProgress(): Promise<void> {
  try {
    const [cosmetics, playerCard, titles] = await Promise.all([
      api.get<CosmeticsResponse>('/cosmetics'),
      api.get<{ equippedCard: number }>('/player-cards').catch(() => null),
      api
        .get<{ titles: Array<{ slug: string; name: string; equipped: boolean }> }>('/game/titles')
        .catch(() => null),
    ]);
    playerProgressStore.set({
      ...cosmetics.progress,
      equippedCard: playerCard?.equippedCard ?? NO_CARD,
      equippedTitle: titles?.titles?.find((row) => row.equipped)?.name ?? null,
    });
  } catch {
    // Left as whatever it was — see module doc.
  }
}
